import { useRef, useEffect, useCallback } from 'react'
import * as d3 from 'd3'
import { useDashboardStore, type DashboardMetric } from '@/stores/dashboardStore'
import type { ChartPoint, ProjectionPoint } from '@/types'

const MARGIN = { top: 20, right: 20, bottom: 40, left: 70 }
const HEIGHT = 320
const MAIN_COLOR = '#2563eb'
const PREV_COLOR = '#93c5fd'
const PROJECTION_COLOR = '#ec4899'

export function DashboardChart() {
  const data = useDashboardStore((s) => s.data)
  const metric = useDashboardStore((s) => s.metric)

  const containerRef = useRef<HTMLDivElement>(null)
  const svgRef = useRef<SVGSVGElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const dataRef = useRef(data)
  const metricRef = useRef(metric)
  dataRef.current = data
  metricRef.current = metric

  const draw = useCallback(() => {
    const chartData = dataRef.current?.chart
    const metric = metricRef.current

    if (!svgRef.current || !containerRef.current || !chartData) return

    const { current, previous, projection } = chartData

    svgRef.current.setAttribute('width', '0')
    const width = containerRef.current.clientWidth
    const innerWidth = width - MARGIN.left - MARGIN.right
    const innerHeight = HEIGHT - MARGIN.top - MARGIN.bottom

    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    svg.attr('width', width).attr('height', HEIGHT)

    if (current.length === 0) return

    const todayStr = new Date().toISOString().slice(0, 10)
    const todayIdx = current.findIndex((p) => p.date >= todayStr)

    const xScale = d3.scaleLinear().domain([0, current.length - 1]).range([0, innerWidth])

    // Current series truncated to today (no flat future extension)
    const currentRendered = todayIdx >= 0 ? current.slice(0, todayIdx + 1) : current

    const currentValues = currentRendered.map((p) => metricValue(p, metric))
    const previousValues = previous.map((p) => metricValue(p, metric))
    const projectionValues = projection ? projection.map((p) => projectionMetricValue(p, metric)) : []

    const maxValue = Math.max(0, ...currentValues, ...previousValues, ...projectionValues)
    const yScale = d3.scaleLinear().domain([0, maxValue * 1.1 || 1]).range([innerHeight, 0])

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`)

    const tickCount = innerWidth < 250 ? 4 : Math.min(current.length, 10)

    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(
        d3.axisBottom(xScale)
          .ticks(tickCount)
          .tickFormat((d) => {
            const idx = Math.round(Number(d))
            return current[idx] ? formatDayLabel(current[idx].date) : ''
          })
      )
      .attr('font-size', 10)

    g.append('g')
      .call(
        d3.axisLeft(yScale)
          .ticks(5)
          .tickFormat((v) => formatTickValue(Number(v), metric))
      )
      .attr('font-size', 10)

    const lineGenerator = d3
      .line<{ index: number; value: number }>()
      .x((p) => xScale(p.index))
      .y((p) => yScale(p.value))
      .curve(d3.curveLinear)

    if (previous.length > 0) {
      g.append('path')
        .datum(previous.map((p, i) => ({ index: i, value: metricValue(p, metric) })))
        .attr('fill', 'none')
        .attr('stroke', PREV_COLOR)
        .attr('stroke-width', 2)
        .attr('d', lineGenerator)
    }

    // Draw actual line only up to today
    if (currentRendered.length > 0) {
      g.append('path')
        .datum(currentRendered.map((p, i) => ({ index: i, value: metricValue(p, metric) })))
        .attr('fill', 'none')
        .attr('stroke', MAIN_COLOR)
        .attr('stroke-width', 2.5)
        .attr('d', lineGenerator)

      // Small circle at the end of today's actual data
      const lastRendered = currentRendered[currentRendered.length - 1]
      const lastIdx = todayIdx >= 0 ? todayIdx : currentRendered.length - 1
      g.append('circle')
        .attr('cx', xScale(lastIdx))
        .attr('cy', yScale(metricValue(lastRendered, metric)))
        .attr('r', 4)
        .attr('fill', MAIN_COLOR)
        .attr('stroke', '#fff')
        .attr('stroke-width', 1.5)
    }

    // Projection line: starts one position before today (anchored at yesterday's cumulative)
    // so there's a visible segment bridging the start of today to the projected end-of-day
    if (projection && projection.length > 0) {
      const projStartIdx = todayIdx >= 0 ? todayIdx : current.length

      const connectorPoint = projStartIdx > 0
        ? [{ index: projStartIdx - 1, value: metricValue(current[projStartIdx - 1], metric) }]
        : []

      const projData = [
        ...connectorPoint,
        ...projection.map((p, i) => ({
          index: projStartIdx + i,
          value: projectionMetricValue(p, metric),
        })),
      ]

      g.append('path')
        .datum(projData)
        .attr('fill', 'none')
        .attr('stroke', PROJECTION_COLOR)
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '6,3')
        .attr('opacity', 0.8)
        .attr('d', lineGenerator)
    }

    const verticalLine = g
      .append('line')
      .attr('stroke', '#bbb')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '4,3')
      .attr('y1', 0)
      .attr('y2', innerHeight)
      .style('display', 'none')

    g.append('rect')
      .attr('width', innerWidth)
      .attr('height', innerHeight)
      .attr('fill', 'none')
      .attr('pointer-events', 'all')
      .on('mousemove', function (event) {
        const [mx] = d3.pointer(event)
        const idx = Math.max(0, Math.min(Math.round(xScale.invert(mx)), current.length - 1))
        const x = xScale(idx)

        verticalLine.attr('x1', x).attr('x2', x).style('display', null)

        if (tooltipRef.current) {
          const isRightHalf = mx > innerWidth / 2
          tooltipRef.current.style.display = 'block'
          tooltipRef.current.style.left = isRightHalf
            ? `${x + MARGIN.left - 220}px`
            : `${x + MARGIN.left + 16}px`
          tooltipRef.current.style.top = `${MARGIN.top}px`
          tooltipRef.current.innerHTML = buildTooltipHTML(
            idx,
            current,
            previous,
            projection,
            metric,
            todayStr,
          )
        }
      })
      .on('mouseleave', function () {
        verticalLine.style('display', 'none')
        if (tooltipRef.current) tooltipRef.current.style.display = 'none'
      })
  }, [])

  useEffect(() => {
    draw()
  }, [data, metric, draw])

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>
    function handleResize() {
      clearTimeout(timeout)
      timeout = setTimeout(draw, 150)
    }
    window.addEventListener('resize', handleResize)
    return () => {
      clearTimeout(timeout)
      window.removeEventListener('resize', handleResize)
    }
  }, [draw])

  if (!data?.chart || data.chart.current.length === 0) {
    return <div className="h-40 flex items-center justify-center text-gray-400">Sem dados para o período</div>
  }

  return (
    <div ref={containerRef} style={{ width: '100%', overflow: 'hidden', position: 'relative' }}>
      <svg ref={svgRef} style={{ display: 'block' }} />
      <div
        ref={tooltipRef}
        style={{
          position: 'absolute',
          display: 'none',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          padding: '8px 12px',
          fontSize: 12,
          pointerEvents: 'none',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          minWidth: 200,
          zIndex: 10,
        }}
      />
    </div>
  )
}

function metricValue(point: ChartPoint, metric: DashboardMetric): number {
  return metric === 'receita' ? point.valorAcumulado : point.matriculasAcumuladas
}

function projectionMetricValue(point: ProjectionPoint, metric: DashboardMetric): number {
  return metric === 'receita' ? point.valorProjetado : point.matriculasProjetadas
}

function buildTooltipHTML(
  idx: number,
  current: ChartPoint[],
  previous: ChartPoint[],
  projection: ProjectionPoint[] | null,
  metric: DashboardMetric,
  todayStr: string,
): string {
  const dot = (color: string) =>
    `<span style="width:8px;height:8px;border-radius:50%;background:${color};display:inline-block;flex-shrink:0"></span>`

  const currentPoint = current[idx]
  const prevPoint = previous[idx]
  const isToday = currentPoint?.date === todayStr
  const isFuture = currentPoint ? currentPoint.date > todayStr : false

  const todayProjectionIdx = projection
    ? current.findIndex((p) => p.date >= todayStr)
    : -1
  const projPoint = projection && todayProjectionIdx >= 0 && idx >= todayProjectionIdx
    ? projection[idx - todayProjectionIdx]
    : null

  const currentValue = currentPoint ? metricValue(currentPoint, metric) : 0
  const prevValue = prevPoint ? metricValue(prevPoint, metric) : 0
  const prevCurrentPoint = idx > 0 ? current[idx - 1] : undefined
  const prevPrevPoint = idx > 0 ? previous[idx - 1] : undefined

  const header = currentPoint
    ? `<div style="font-weight:600;margin-bottom:4px">${formatDayFull(currentPoint.date)}</div>`
    : ''

  // Show actual value for past days and today; not for future
  const currentRow = currentPoint && !isFuture
    ? buildRow(
        dot(MAIN_COLOR),
        formatPeriodLabel(currentPoint.date),
        formatMetricValue(currentValue, metric),
        prevCurrentPoint ? currentValue - metricValue(prevCurrentPoint, metric) : null,
        metric,
      )
    : ''

  // Show projected value for today and future
  const projRow = projPoint && (isToday || isFuture)
    ? buildProjectionRow(dot(PROJECTION_COLOR), 'Projeção', formatMetricValue(projectionMetricValue(projPoint, metric), metric))
    : ''

  const prevRow = prevPoint
    ? buildRow(
        dot(PREV_COLOR),
        formatPeriodLabel(prevPoint.date, true),
        formatMetricValue(prevValue, metric),
        prevPrevPoint ? prevValue - metricValue(prevPrevPoint, metric) : null,
        metric,
      )
    : ''

  return header + currentRow + projRow + prevRow
}

function buildRow(dotHtml: string, label: string, value: string, delta: number | null, metric: DashboardMetric): string {
  const deltaHtml = delta !== null && delta !== 0
    ? formatDeltaSpan(delta, metric)
    : ''

  return (
    `<div style="display:flex;align-items:center;gap:6px;margin-top:4px">` +
    `${dotHtml}<span style="color:#666;font-size:11px">${label}</span>` +
    `<span style="margin-left:auto;font-weight:500">${value}</span>` +
    `</div>` +
    `<div style="padding-left:14px;font-size:11px;color:#999">${deltaHtml}</div>`
  )
}

function buildProjectionRow(dotHtml: string, label: string, value: string): string {
  return (
    `<div style="display:flex;align-items:center;gap:6px;margin-top:4px">` +
    `${dotHtml}<span style="color:#666;font-size:11px">${label}</span>` +
    `<span style="margin-left:auto;font-weight:500">${value}</span>` +
    `</div>`
  )
}

function formatMetricValue(value: number, metric: DashboardMetric): string {
  if (metric === 'receita') {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value)
  }
  return String(Math.round(value))
}

function formatDeltaSpan(delta: number, metric: DashboardMetric): string {
  const arrow = delta > 0 ? '▲' : '▼'
  const color = delta > 0 ? '#22c55e' : '#ef4444'
  return `<span style="color:${color}">${arrow} ${formatMetricValue(Math.abs(delta), metric)}</span>`
}

function formatTickValue(value: number, metric: DashboardMetric): string {
  if (metric === 'receita') {
    return value >= 1000 ? `${(value / 1000).toFixed(0)}k` : String(value)
  }
  return String(Math.round(value))
}

function formatDayLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
}

function formatDayFull(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  const weekday = d.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '')
  const dayMonth = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
  return `${weekday} ${dayMonth}`
}

function formatPeriodLabel(dateStr: string, isPrevious = false): string {
  const d = new Date(dateStr + 'T00:00:00')
  const dayMonth = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
  const weekday = d.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '')
  const label = `${dayMonth} ${weekday}`
  return isPrevious ? `${label} (anterior)` : label
}
